import { buildOrgxApiUrl, resolveOrgxAuth, type ResolveOrgxAuthOptions } from "./auth.js";
import { isRecord } from "./fs.js";
import { fetchWithRetry } from "./network.js";

export interface OrgxWorkspace {
  id: string;
  name: string;
  description?: string;
  isDefault: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateWorkspaceInput {
  name: string;
  description?: string;
}

export interface SetDefaultWorkspaceInput {
  id: string;
}

export interface OrgxWorkspaceClientOptions extends ResolveOrgxAuthOptions {}

async function requireOrgxAuth(options: OrgxWorkspaceClientOptions = {}) {
  const auth = await resolveOrgxAuth(options);
  if (!auth) {
    throw new Error(
      "No OrgX API key configured. Run `wizard auth login` or `wizard auth set-key <oxk_...>` first.",
    );
  }

  return auth;
}

async function parseResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function parseWorkspace(value: unknown): OrgxWorkspace | null {
  if (!isRecord(value)) return null;

  const id = typeof value.id === "string" ? value.id : null;
  const name = typeof value.name === "string" ? value.name : null;
  if (!id || !name) return null;

  return {
    id,
    name,
    ...(typeof value.description === "string" && value.description.trim().length > 0
      ? { description: value.description }
      : {}),
    isDefault: value.is_default === true,
    ...(typeof value.created_at === "string" ? { createdAt: value.created_at } : {}),
    ...(typeof value.updated_at === "string" ? { updatedAt: value.updated_at } : {}),
  };
}

function sortWorkspaces(workspaces: OrgxWorkspace[]): OrgxWorkspace[] {
  return [...workspaces].sort((left, right) => {
    if (left.isDefault !== right.isDefault) {
      return left.isDefault ? -1 : 1;
    }

    const nameCompare = left.name.localeCompare(right.name);
    if (nameCompare !== 0) {
      return nameCompare;
    }

    return left.id.localeCompare(right.id);
  });
}

function parseWorkspaceList(payload: unknown): OrgxWorkspace[] {
  if (!isRecord(payload) || !Array.isArray(payload.data)) {
    return [];
  }

  return sortWorkspaces(payload.data.map(parseWorkspace).filter((item): item is OrgxWorkspace => item !== null));
}

function parseWorkspaceResponse(payload: unknown): OrgxWorkspace | null {
  if (!isRecord(payload)) return null;
  return parseWorkspace(payload.data);
}

function parseCurrentWorkspaceResponse(payload: unknown): OrgxWorkspace | null {
  if (!isRecord(payload)) return null;

  const id = typeof payload.id === "string" ? payload.id : null;
  const name = typeof payload.name === "string" ? payload.name : null;
  if (!id || !name) return null;

  return {
    id,
    name,
    isDefault: true,
    ...(typeof payload.createdAt === "string" ? { createdAt: payload.createdAt } : {}),
  };
}

function buildRequestHeaders(apiKey: string): Record<string, string> {
  return {
    Authorization: `Bearer ${apiKey}`,
    "Content-Type": "application/json",
  };
}

function formatHttpError(status: number, body: unknown): string {
  if (typeof body === "string" && body.trim().length > 0) {
    return `HTTP ${status}: ${body}`;
  }

  if (isRecord(body) && typeof body.error === "string" && body.error.trim().length > 0) {
    return `HTTP ${status}: ${body.error}`;
  }

  return `HTTP ${status}`;
}

export async function listWorkspaces(
  options: OrgxWorkspaceClientOptions = {},
): Promise<OrgxWorkspace[]> {
  const auth = await requireOrgxAuth(options);
  const url = buildOrgxApiUrl("/entities?type=workspace&limit=100", auth.baseUrl);
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${auth.apiKey}`,
    },
  });
  const body = await parseResponseBody(response);

  if (!response.ok) {
    throw new Error(`Failed to list workspaces. ${formatHttpError(response.status, body)}`);
  }

  return parseWorkspaceList(body);
}

export async function getCurrentWorkspace(
  options: OrgxWorkspaceClientOptions = {},
): Promise<OrgxWorkspace | null> {
  const auth = await requireOrgxAuth(options);
  const url = buildOrgxApiUrl("/v1/workspaces/current", auth.baseUrl);
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${auth.apiKey}`,
    },
  });
  const body = await parseResponseBody(response);

  if (response.ok) {
    const workspace = parseCurrentWorkspaceResponse(body);
    if (!workspace) {
      throw new Error("OrgX returned an unexpected current workspace payload.");
    }
    return workspace;
  }

  if (response.status === 401 || response.status === 404) {
    const workspaces = await listWorkspaces(options);
    return workspaces[0] ?? null;
  }

  throw new Error(`Failed to load current workspace. ${formatHttpError(response.status, body)}`);
}

export async function createWorkspace(
  input: CreateWorkspaceInput,
  options: OrgxWorkspaceClientOptions = {},
): Promise<OrgxWorkspace> {
  const name = input.name.trim();
  if (!name) {
    throw new Error("Workspace name is required.");
  }

  const auth = await requireOrgxAuth(options);
  const url = buildOrgxApiUrl("/entities", auth.baseUrl);
  const payload = {
    type: "workspace",
    name,
    ...(input.description?.trim()
      ? { description: input.description.trim() }
      : {}),
  };

  const response = await fetch(url, {
    method: "POST",
    headers: buildRequestHeaders(auth.apiKey),
    body: JSON.stringify(payload),
    signal: AbortSignal.timeout(7_000),
  });
  const body = await parseResponseBody(response);

  if (!response.ok) {
    throw new Error(`Failed to create workspace. ${formatHttpError(response.status, body)}`);
  }

  const workspace = parseWorkspaceResponse(body);
  if (!workspace) {
    throw new Error("OrgX returned an unexpected workspace payload.");
  }

  return workspace;
}

async function updateWorkspace(
  input: SetDefaultWorkspaceInput,
  patch: Record<string, unknown>,
  options: OrgxWorkspaceClientOptions = {},
): Promise<OrgxWorkspace> {
  const auth = await requireOrgxAuth(options);
  const url = buildOrgxApiUrl("/entities", auth.baseUrl);
  const response = await fetch(url, {
    method: "PATCH",
    headers: buildRequestHeaders(auth.apiKey),
    body: JSON.stringify({
      type: "workspace",
      id: input.id,
      ...patch,
    }),
    signal: AbortSignal.timeout(7_000),
  });
  const body = await parseResponseBody(response);

  if (!response.ok) {
    throw new Error(`Failed to update workspace. ${formatHttpError(response.status, body)}`);
  }

  const workspace = parseWorkspaceResponse(body);
  if (!workspace) {
    throw new Error("OrgX returned an unexpected workspace update payload.");
  }

  return workspace;
}

export async function setDefaultWorkspace(
  input: SetDefaultWorkspaceInput,
  options: OrgxWorkspaceClientOptions = {},
): Promise<{ changed: boolean; workspace: OrgxWorkspace }> {
  const workspaceId = input.id.trim();
  if (!workspaceId) {
    throw new Error("Workspace id is required.");
  }

  const workspaces = await listWorkspaces(options);
  const target = workspaces.find((workspace) => workspace.id === workspaceId);
  if (!target) {
    throw new Error(`Workspace ${workspaceId} was not found in the current OrgX account.`);
  }

  if (target.isDefault) {
    return {
      changed: false,
      workspace: target,
    };
  }

  const currentDefault = workspaces.find((workspace) => workspace.isDefault);
  if (currentDefault) {
    await updateWorkspace({ id: currentDefault.id }, { is_default: false }, options);
  }

  try {
    const updated = await updateWorkspace({ id: workspaceId }, { is_default: true }, options);
    return {
      changed: true,
      workspace: updated,
    };
  } catch (error) {
    if (currentDefault) {
      try {
        await updateWorkspace({ id: currentDefault.id }, { is_default: true }, options);
      } catch {
        // Preserve the original failure while best-effort restoring the prior default.
      }
    }
    throw error;
  }
}
